'use client'

import {
  ColorDecorativePurple70,
  SizeBorder2,
  SizeSpacing2,
  SizeSpacing3,
  SizeSpacing8,
  SizeSpacingHalf,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import {
  Paper,
  PaperElevations,
} from '@snowball-tech/fractal/dist/src/components/Paper'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import formatDuration from 'format-duration'

import isEmpty from 'lodash/fp/isEmpty'
import isNumber from 'lodash/fp/isNumber'

import type { ChildrenProps, Edition } from '../../types'

import { getEditionUrl } from '../../helpers/navigation'
import useTranslations from '../../hooks/useTranslations'
import useUrls from '../../hooks/useUrls'
import { asPx, spacings } from '../../styles/helpers'
import MediaDisplay from '../Edition/MediaDisplay'
import LegacyTable from '../LegacyTable'

export const TYPE = 'audio'

type Props = {
  editionUuid: string
  duration?: number
  editionSlug?: string
  forHtml?: boolean
  fullWidth?: boolean
  id?: string
  url?: string
} & Partial<ChildrenProps>
export default function Audio({
  children: caption,
  duration,
  editionSlug,
  editionUuid,
  forHtml = false,
  fullWidth = true,
  id,
  url,
}: Props) {
  const $t = useTranslations()

  const assetsURLs = useUrls()

  const htmlId = isEmpty(id) ? undefined : `${TYPE}-${id}`

  const hasUrl = !isEmpty(url)

  if (!hasUrl) {
    return <div className="hidden" />
  }

  const editionUrl = `${getEditionUrl({ slug: editionSlug, uuid: editionUuid } as Edition)}#${htmlId}`

  const formattedDuration =
    isNumber(duration) && duration > 0
      ? formatDuration(duration * 1000, { leading: true })
      : ''

  const buttonCellStyles = { width: `${spacings[6] + spacings[2]}px` }

  return forHtml ? (
    <LegacyTable
      id={htmlId}
      col
      data-duration={duration}
      data-type={TYPE}
      data-url={url}
      defaultCellStyle={{ textAlign: 'center' }}
      gap={spacings[1]}
      style={{ marginBottom: asPx(SizeSpacing3, { withUnit: true }) }}
    >
      <LegacyTable
        cellStyle={[
          {
            textWrap: 'nowrap',
            width: '100%',
          },
          buttonCellStyles,
        ]}
        center
        className="preserve-legacy-media-block"
        elevation={PaperElevations.Elevated}
        importantWidth={false}
        style={{
          backgroundColor: ColorDecorativePurple70,
          height: '100%',
          maxWidth: 'min(100%, 400px)',
          minHeight: asPx(SizeSpacing8, { withUnit: true }),
          width: '100%',
        }}
      >
        <Typography
          href={editionUrl}
          inlineStyle
          style={{
            display: 'block',
            margin: `${asPx(SizeSpacingHalf, { withUnit: true })} 0 0 0`,
            overflow: 'hidden',
            padding: asPx(SizeSpacing3, { withUnit: true }),
            paddingLeft: asPx(SizeSpacing2, { withUnit: true }),
            textAlign: 'left',
            textDecoration: 'none',
            textOverflow: 'ellipsis',
            whiteSpace: 'break-spaces',
            width: '100%',
          }}
          target="_blank"
          title={$t('edition.audio.duration', {
            duration: formattedDuration,
          })}
          variant="body-1-link"
        >
          {$t('edition.audio.listen')}
        </Typography>

        <Typography
          element="a"
          href={editionUrl}
          inlineStyle
          style={{
            display: 'block',
            padding: `${asPx(SizeSpacing2, { withUnit: true })} ${asPx(SizeSpacing2, { withUnit: true })} ${asPx(SizeSpacing2, { withUnit: true })} 0`,
            textDecoration: 'none',
            width: '100%',
          }}
          target="_blank"
        >
          <Button
            fullStyle
            icon={
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt=""
                src={`${assetsURLs.assets}/images/icon-play.png`}
                style={{
                  height: asPx(SizeSpacing3, { withUnit: true }),
                  width: asPx(SizeSpacing3, { withUnit: true }),
                }}
              />
            }
            iconOnly
            inlineStyle
            label={$t('edition.audio.listen_with_duration', {
              duration: formattedDuration,
            })}
            style={{
              borderWidth: asPx(SizeBorder2, { withUnit: true }),
            }}
          />
        </Typography>
      </LegacyTable>
      {caption}
    </LegacyTable>
  ) : (
    <div className="w-full">
      <div className="flex flex-col gap-1">
        <Paper
          className={cj(
            'relative flex flex-row size-full justify-center p-0 pointer-events-auto max-h-unset min-h-8 items-center mx-auto min-w-[300px] bg-decorative-purple-70',
            fullWidth ? '' : 'max-w-[600px]',
          )}
          elevation={PaperElevations.Elevated}
        >
          <MediaDisplay
            id={htmlId}
            duration={duration}
            fullWidth={fullWidth}
            type="audio"
            url={url}
            withLoader
          />
        </Paper>

        {caption}
      </div>
    </div>
  )
}
